/**
 * PendoProvider
 * Loads the Pendo SDK and initialises/identifies visitor+account.
 * Renders children passthrough — no extra DOM nodes.
 *
 * Pattern follows e-invoicing-portal's PendoProvider, adapted for
 * the standalone app's FlowSession context.
 */
import React from "react";
import { useTranslation } from "react-i18next";

import { environment } from "@constants/environment";
import { useFlowSession } from "@providers/FlowSessionProvider";

const PRODUCT_NAME = "Sage E-Invoicing";

function isPendoEnabled(): boolean {
  return Boolean(
    environment.ENABLE_PENDO_TRACKING && environment.PENDO_API_KEY
  );
}

interface PendoProviderProps {
  children: React.ReactNode;
}

const PendoProvider: React.FC<PendoProviderProps> = ({ children }) => {
  const pendoRef = React.useRef<{
    isLoaded: boolean;
    isInitialized: boolean;
  }>({ isLoaded: false, isInitialized: false });

  const { session } = useFlowSession();
  const { i18n } = useTranslation();

  // 1. Load the Pendo agent script (one-time)
  React.useEffect(() => {
    if (
      isPendoEnabled() &&
      !pendoRef.current.isLoaded &&
      !window.pendo?.apiKey
    ) {
      window.loadPendo?.(environment.PENDO_API_KEY);
      pendoRef.current.isLoaded = true;
    }
  }, []);

  // 2. Initialise / re-identify when session or language changes
  React.useEffect(() => {
    if (!isPendoEnabled() || !session) return;

    const pendoConfig = {
      visitor: {
        id: session.sessionId,
        product: PRODUCT_NAME,
        displayLanguage: i18n.language
      },
      account: {
        id: session.groupKey,
        name: session.datasetId,
        product: PRODUCT_NAME
      }
    };

    if (!pendoRef.current.isInitialized) {
      window.pendo?.initialize(pendoConfig);
      pendoRef.current.isInitialized = true;
    } else {
      window.pendo?.identify(pendoConfig);
    }
  }, [session, i18n.language]);

  return <>{children}</>;
};

export default PendoProvider;
